import type { ComponentPropsWithRef, ElementType } from 'react';
import { Slot } from '@radix-ui/react-slot';

import { cn } from '@/lib/utils';
import { getTypographyVariantSizeClass, typographyVariants, type TypographyVariantsProps } from './typography.variants';

type As = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div' | 'blockquote' | 'code';

export interface TypographyProps extends ComponentPropsWithRef<'div'>, TypographyVariantsProps {
    asChild?: boolean;
    as?: As;
}

function getDefaultElement(variant: TypographyProps['variant']): As {
    switch (variant) {
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'span':
            return variant;
        case 'display-lg':
        case 'display':
        case 'display-sm':
        case 'amiri-display':
            return 'h1';
        case 'amiri-h1':
            return 'h1';
        case 'amiri-subheading':
            return 'h2';
        case 'blockquote':
            return 'blockquote';
        case 'inline-code':
            return 'code';
        case 'product-title':
            return 'h3';
        default:
            return 'p';
    }
}

export const Typography = ({
    className,
    variant,
    align,
    weight,
    asChild = false,
    as,
    ref,
    ...props
}: TypographyProps) => {
    const Comp = (asChild ? Slot : as || getDefaultElement(variant)) as ElementType;

    return (
        <Comp
            data-slot="typography"
            data-variant={variant ?? 'p'}
            className={cn(
                typographyVariants({ variant, align, weight }),
                getTypographyVariantSizeClass(variant),
                className
            )}
            ref={ref as never}
            {...props}
        />
    );
};
